import { logger } from '@/utils/logger';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi } from '@/api/auth/auth';
import { useIdleTimeout } from '@/hooks/auth/useIdleTimeout';

interface AuthGuardProps {
    children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const location = useLocation();

    useIdleTimeout(isAuthenticated);

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                setIsAuthenticated(false);
                setIsChecking(false);
                return;
            }

            try {
                if (!authApi.isAuthenticated()) {
                    throw new Error('Token expired locally');
                }
                const isValid = await authApi.verifyToken();
                if (!isValid) {
                    throw new Error('Token revoked by server');
                }

                setIsAuthenticated(true);
            } catch (error) {
                logger.error('Token validation error:', error);
                localStorage.removeItem('auth_token');
                localStorage.removeItem('refresh_token');
                setIsAuthenticated(false);
                if (error.message === 'Token expired locally') {
                    toast.error('Your session has expired. Please login again.');
                } else if (error.message === 'Token revoked by server') {
                    toast.error('Your session is no longer valid. Please login again.');
                }
            }

            setIsChecking(false);
        };

        checkAuth();
    }, [location.pathname]);

    if (isChecking) {

        return (
            <div className="h-screen flex items-center justify-center">
                <p>Loading...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }
    return <>{children}</>;
};

export default AuthGuard;